import { useCallback, useEffect, useMemo, useState } from 'react';

import { CartContext } from './cart-context';

const STORAGE_KEY = 'ayat.cart';

// A line is identified by slug + size, so 50ml and 100ml of the same scent are
// distinct rows with their own quantity.
const lineKey = (slug, size) => `${slug}-${size}`;

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(readStored);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Ignore storage failures (private mode / quota) — cart stays in memory.
    }
  }, [items]);

  const addItem = useCallback((item, qty = 1) => {
    const key = lineKey(item.slug, item.size);
    setItems((prev) => {
      const existing = prev.find((line) => line.key === key);
      if (existing) {
        return prev.map((line) =>
          line.key === key ? { ...line, qty: line.qty + qty } : line,
        );
      }
      return [...prev, { ...item, key, qty }];
    });
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((line) => line.key !== key));
  }, []);

  const setQty = useCallback((key, qty) => {
    setItems((prev) =>
      prev
        .map((line) => (line.key === key ? { ...line, qty: Math.max(0, qty) } : line))
        .filter((line) => line.qty > 0),
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const { count, subtotal } = useMemo(
    () =>
      items.reduce(
        (acc, line) => {
          acc.count += line.qty;
          acc.subtotal += line.price * line.qty;
          return acc;
        },
        { count: 0, subtotal: 0 },
      ),
    [items],
  );

  const value = useMemo(
    () => ({
      items,
      count,
      subtotal,
      isOpen,
      addItem,
      removeItem,
      setQty,
      clear,
      openCart,
      closeCart,
    }),
    [items, count, subtotal, isOpen, addItem, removeItem, setQty, clear, openCart, closeCart],
  );

  return <CartContext value={value}>{children}</CartContext>;
}
